import { objectKeys } from '@/utils/object';
import { CONFIG_ORIGIN_PARAM_KEY, CONFIG_ORIGINS } from './const';
import type { ConfigOriginKey } from './domain';
import { AppErrorCode } from '../error-handling/error-codes';
import { AppError } from '../error-handling';

/**
 * Retrieves the configuration origin from URL search parameters.
 *
 * @param {URLSearchParams} searchParams - The URL search parameters containing the configuration origin key.
 * @returns {ConfigOrigin} The configuration origin value. If the parameter is not found or invalid,
 *                         defaults to the 'configurator' origin.
 *
 * @example
 * const searchParams = new URLSearchParams('config-origin=seac');
 * getConfigOriginFromSearchParams(searchParams); // Returns the SEAC config origin
 *
 * @example
 * const searchParams = new URLSearchParams();
 * getConfigOriginFromSearchParams(searchParams); // Returns the configurator origin (default)
 */
export function getConfigOriginFromSearchParams(searchParams: URLSearchParams) {
	const configOriginParam = searchParams.get(CONFIG_ORIGIN_PARAM_KEY);

	if (!configOriginParam) {
		throw new AppError({
			code: AppErrorCode.MISSING_PARAM_CONFIG_ORIGIN,
			message: `Missing required parameter: ${CONFIG_ORIGIN_PARAM_KEY}`,
			name: AppErrorCode.MISSING_PARAM_CONFIG_ORIGIN
		});
	}

	if (!objectKeys(CONFIG_ORIGINS).includes(configOriginParam as ConfigOriginKey)) {
		throw new AppError({
			code: AppErrorCode.MISSING_PARAM_CONFIG_ORIGIN,
			message: `Invalid config origin: ${configOriginParam}`,
			name: AppErrorCode.MISSING_PARAM_CONFIG_ORIGIN
		});
	}

	return CONFIG_ORIGINS[configOriginParam as ConfigOriginKey];
}

/**
 * Retrieves the configuration origin from URL search parameters. and
 * checks if the origin is SEAC.
 * @param {LooseSearchParams} searchParams - The URL search parameters containing the configuration origin key.
 * @returns {boolean} True if the origin is SEAC, false otherwise.
 */
export function isSeacOrigin(searchParams: LooseSearchParams): boolean {
	const origin = safelyGetOriginValue(searchParams);

	return origin === CONFIG_ORIGINS.seac.key;
}

/**
 * Retrieves the configuration origin from URL search parameters. and
 * checks if the origin is the configurator.
 * @param {LooseSearchParams} searchParams - The URL search parameters containing the configuration origin key.
 * @returns {boolean} True if the origin is the configurator, false otherwise.
 */
export function isConfigOrigin(searchParams: LooseSearchParams): boolean {
	const origin = safelyGetOriginValue(searchParams);

	return origin === CONFIG_ORIGINS.configurator.key;
}

function safelyGetOriginValue(searchParams: LooseSearchParams): (typeof CONFIG_ORIGINS)[keyof typeof CONFIG_ORIGINS]['key'] {
	const urlSearchParams =
		searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams as Record<string, string>);
	const configOriginParam = urlSearchParams.get(CONFIG_ORIGIN_PARAM_KEY);

	if (!configOriginParam) {
		throw new AppError({
			code: AppErrorCode.MISSING_PARAM_CONFIG_ORIGIN,
			message: `Missing required parameter: ${CONFIG_ORIGIN_PARAM_KEY}`,
			name: AppErrorCode.MISSING_PARAM_CONFIG_ORIGIN
		});
	}

	const configOrigin = Object.values(CONFIG_ORIGINS).find((origin) => origin.key === configOriginParam);

	if (!configOrigin) {
		throw new AppError({
			code: AppErrorCode.MISSING_PARAM_CONFIG_ORIGIN,
			message: `Invalid config origin: ${configOriginParam}`,
			name: AppErrorCode.MISSING_PARAM_CONFIG_ORIGIN
		});
	}
	return configOrigin!.key;
}

type LooseSearchParams = URLSearchParams | Record<string, string | undefined> | undefined;
